/**
 * Site access helpers: projectSiteAccess allowlist and first-site fallbacks.
 */

/**
 * True when the user has a per-site allowlist for this project.
 * An empty allowlist means no sites (same rule as alert delivery).
 * @param {object|null} user
 * @param {string|null} projectId
 * @returns {boolean}
 */
export function hasProjectSiteAllowlist(user, projectId) {
	const psa = user && user.projectSiteAccess;
	return !!(psa && projectId && Object.prototype.hasOwnProperty.call(psa, projectId));
}

/**
 * Object id for a site row from /api or the store.
 * @param {object|null} site
 * @returns {string|null}
 */
export function siteObjectId(site) {
	if (!site) return null;
	return site.objectId || site.id || null;
}

/**
 * Normalize a projectSiteAccess id list to string objectIds.
 * @param {Array} raw
 * @returns {string[]}
 */
function allowlistIds(raw) {
	const arr = Array.isArray(raw) ? raw : [];
	const ids = [];
	for (let i = 0; i < arr.length; i++) {
		const item = arr[i];
		if (!item) continue;
		if (typeof item === 'string') ids.push(item);
		else if (item.objectId) ids.push(item.objectId);
		else if (item.id) ids.push(item.id);
	}
	return ids;
}

/**
 * Sites the user may open in this project.
 * @param {Array} sites
 * @param {object|null} user
 * @param {string|null} projectId
 * @returns {Array}
 */
export function sitesUserCanAccess(sites, user, projectId) {
	const all = Array.isArray(sites) ? sites : [];
	const hasAllowlist = hasProjectSiteAllowlist(user, projectId);
	const allowedIds = hasAllowlist ? allowlistIds(user.projectSiteAccess[projectId]) : null;
	const out = [];
	for (let i = 0; i < all.length; i++) {
		const s = all[i];
		if (!s) continue;
		const id = siteObjectId(s);
		if (!id) continue;
		if (s.hidden === true && !hasAllowlist) continue;
		if (hasAllowlist && allowedIds.indexOf(id) < 0) continue;
		if (projectId && s.project) {
			const spId = s.project.objectId || s.project.id || s.project;
			if (spId && spId !== projectId) continue;
		}
		out.push(s);
	}
	return out;
}

/**
 * First site the user can access (store / API order).
 * @param {Array} sites
 * @param {object|null} user
 * @param {string|null} projectId
 * @returns {object|null}
 */
export function firstAccessibleSite(sites, user, projectId) {
	const list = sitesUserCanAccess(sites, user, projectId);
	return list[0] || null;
}

/**
 * True when siteId is in the accessible list.
 * @param {string|null} siteId
 * @param {Array} accessibleSites
 * @returns {boolean}
 */
export function isAccessibleSiteId(siteId, accessibleSites) {
	if (!siteId) return false;
	const list = Array.isArray(accessibleSites) ? accessibleSites : [];
	for (let i = 0; i < list.length; i++) {
		if (siteObjectId(list[i]) === siteId) return true;
	}
	return false;
}
